import React, { useEffect, useState } from 'react';
import { api } from '@/api/client';
import type { VehicleFilterEvent } from '@/api/types';
import { CollapsiblePanel } from '@/components/common';
import StatusBadge from '@/components/ui/StatusBadge';
import SourceChips from '@/components/ui/SourceChips';
import { TruncatedCell } from '@/components/table';

type Props = {
  executionId: string;
  executionLabel?: string;
  onClose: () => void;
};

function formatTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function decisionVariant(decision: string): 'success' | 'danger' | 'warning' | 'neutral' {
  if (decision === 'accepted') return 'success';
  if (decision === 'rejected') return 'danger';
  if (decision === 'unknown') return 'warning';
  return 'neutral';
}

function formatRejectionReason(reason: string | null | undefined): string {
  if (!reason) return '—';
  if (reason.startsWith('missing_trim:')) {
    const required = reason.split(':', 2)[1];
    return required ? `required trim ${required} was missing` : 'trim mismatch';
  }
  if (reason.startsWith('conflicting_trim:')) {
    const conflict = reason.split(':', 2)[1];
    return conflict ? `conflicting trim ${conflict} in listing` : 'trim mismatch';
  }
  if (reason.startsWith('missing_model:')) {
    const required = reason.split(':', 2)[1];
    return required ? `required model ${required} was missing` : 'model mismatch';
  }
  if (reason.startsWith('conflicting_model:')) {
    const conflict = reason.split(':', 2)[1];
    return conflict ? `conflicting model ${conflict} in listing` : 'model mismatch';
  }
  return reason;
}

export default function VehicleFilterDiagnosticsDrawer({
  executionId,
  executionLabel,
  onClose,
}: Props) {
  const [items, setItems] = useState<VehicleFilterEvent[]>([]);
  const [selected, setSelected] = useState<VehicleFilterEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void api.searchExecutions
      .listVehicleFilters(executionId)
      .then((data) => {
        if (!cancelled) {
          setItems(data.items);
          setSelected(data.items[0] ?? null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load vehicle filter diagnostics');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [executionId]);

  return (
    <>
      <button
        type="button"
        className="job-drawer-backdrop"
        aria-label="Close vehicle filter diagnostics"
        onClick={onClose}
      />
      <aside className="job-detail-drawer" aria-label="Vehicle filter diagnostics">
        <header className="job-detail-drawer__header">
          <div>
            <h2>Vehicle filter diagnostics</h2>
            {executionLabel ? <p className="muted">{executionLabel}</p> : null}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </header>

        {loading ? <p className="muted">Loading filter events…</p> : null}
        {error ? <p className="text-danger">{error}</p> : null}

        {!loading && !error && items.length === 0 ? (
          <p className="muted">No rejected or unverified listings recorded for this execution.</p>
        ) : null}

        {!loading && items.length > 0 ? (
          <div className="vehicle-filter-drawer-layout">
            <section className="job-detail-drawer__section">
              <h3>Filtered listings ({items.length})</h3>
              <ul className="vehicle-filter-list">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`vehicle-filter-list__item${selected?.id === item.id ? ' is-selected' : ''}`}
                      onClick={() => setSelected(item)}
                    >
                      <span className="vehicle-filter-list__title">
                        {item.listing_title || item.listing_url}
                      </span>
                      <StatusBadge variant={decisionVariant(item.decision)}>
                        {item.decision}
                      </StatusBadge>
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            {selected ? (
              <section className="job-detail-drawer__section">
                <h3>Listing evaluation</h3>
                <dl className="detail-grid">
                  <dt>Source</dt>
                  <dd>
                    {selected.source_name ? (
                      <SourceChips sources={[selected.source_name]} variant="compact" />
                    ) : (
                      '—'
                    )}
                  </dd>
                  <dt>Generated URL</dt>
                  <dd>
                    {selected.seed_url ? (
                      <TruncatedCell value={selected.seed_url} maxWidth={360} />
                    ) : (
                      '—'
                    )}
                  </dd>
                  <dt>Listing</dt>
                  <dd>
                    <TruncatedCell value={selected.listing_url} maxWidth={360} />
                  </dd>
                  <dt>Decision</dt>
                  <dd>
                    <StatusBadge variant={decisionVariant(selected.decision)}>
                      {selected.decision}
                    </StatusBadge>
                  </dd>
                  <dt>Reason</dt>
                  <dd>{formatRejectionReason(selected.rejection_reason)}</dd>
                  <dt>Recorded</dt>
                  <dd>{formatTime(selected.created_at)}</dd>
                </dl>

                <DiagnosticsCollapsible title="Raw values" panelId="raw-values" defaultExpanded>
                  <pre className="code-block">{JSON.stringify(selected.raw_attributes, null, 2)}</pre>
                </DiagnosticsCollapsible>
                <DiagnosticsCollapsible title="Canonical catalog" panelId="canonical-catalog">
                  <pre className="code-block">
                    {JSON.stringify(
                      Object.fromEntries(
                        Object.entries(selected.normalized_attributes ?? {}).filter(([key]) =>
                          key.startsWith('vehicle_') || key === 'catalog_display',
                        ),
                      ),
                      null,
                      2,
                    )}
                  </pre>
                </DiagnosticsCollapsible>
                <DiagnosticsCollapsible title="Provider fields" panelId="provider-fields">
                  <pre className="code-block">
                    {JSON.stringify(
                      Object.fromEntries(
                        Object.entries(selected.normalized_attributes ?? {}).filter(([key]) =>
                          key.startsWith('provider_'),
                        ),
                      ),
                      null,
                      2,
                    )}
                  </pre>
                </DiagnosticsCollapsible>
                <DiagnosticsCollapsible title="Normalized values" panelId="normalized-values">
                  <pre className="code-block">
                    {JSON.stringify(selected.normalized_attributes, null, 2)}
                  </pre>
                </DiagnosticsCollapsible>
                <DiagnosticsCollapsible title="Filter evaluation" panelId="filter-evaluation" defaultExpanded>
                  <pre className="code-block">
                    {JSON.stringify(selected.filter_evaluations, null, 2)}
                  </pre>
                </DiagnosticsCollapsible>
                <DiagnosticsCollapsible title="Requested filters" panelId="requested-filters">
                  <pre className="code-block">
                    {JSON.stringify(selected.requested_filters, null, 2)}
                  </pre>
                </DiagnosticsCollapsible>
              </section>
            ) : null}
          </div>
        ) : null}
      </aside>
    </>
  );
}

function DiagnosticsCollapsible({
  title,
  panelId,
  children,
  defaultExpanded = false,
}: {
  title: string;
  panelId: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) {
  return (
    <CollapsiblePanel
      id={panelId}
      storageScope="vehicle-filter-diagnostics"
      title={title}
      defaultExpanded={defaultExpanded}
      className="collapsible-panel--drawer-nested"
    >
      {children}
    </CollapsiblePanel>
  );
}
